import React from 'react';
import ReactApexChart from 'react-apexcharts';

const LanguageWiseUser = ({ languageData = [] }) => {
  const [imageErrors, setImageErrors] = React.useState({});

  const processedData = React.useMemo(() => {
    if (!languageData || !Array.isArray(languageData)) return [];

    const topLanguages = [...languageData]
      .filter((item) => item && item.UserCount > 0)
      .sort((a, b) => b.UserCount - a.UserCount)
      .slice(0, 5);

    const totalUsers = topLanguages.reduce((sum, item) => sum + item.UserCount, 0);

    return topLanguages.map((item, index) => ({
      ...item,
      rank: index + 1,
      percentage: totalUsers > 0 ? Math.round((item.UserCount / totalUsers) * 100) : 0,
      color: ['#1B5E20', '#2E7D32', '#388E3C', '#4CAF50', '#81C784'][index],
    }));
  }, [languageData]);

  const handleImageError = (languageId) => {
    setImageErrors((prev) => ({ ...prev, [languageId]: true }));
  };

  const getFlagUrl = (item) => {
    if (!item.ImageOfFlag) return null;
    return `https://api.soksan-dss.com/uploads/flag/${item.ImageOfFlag}`;
  };

  const chartSeries = processedData.map((item) => item.UserCount);
  const chartLabels = processedData.map((item) => item.LanguageName);
  const chartColors = processedData.map((item) => item.color);
  const totalUsers = processedData.reduce((sum, item) => sum + item.UserCount, 0);

  const doughnutOptions = {
    chart: {
      type: 'donut',
      height: 220,
      toolbar: { show: false },
    },
    colors: chartColors,
    labels: chartLabels,
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              fontSize: '12px',
              color: '#126B3A',
              formatter: function () {
                return totalUsers.toString();
              },
            },
          },
        },
      },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: {
      y: {
        formatter: function (val, { seriesIndex }) {
          const item = processedData[seriesIndex];
          return item ? `${val} users (${item.percentage}%)` : `${val} users`;
        },
      },
    },
  };

  if (processedData.length === 0) {
    return (
      <div className="col-12 col-md-6 col-xl-3">
        <div className="card h-100">
          <div className="card-body text-center">
            <h6 className="text-lg mb-3" style={{ color: '#1B5E20', fontWeight: 'bold' }}>
              Top Languages
            </h6>
            <p className="text-muted">No language data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-12 col-md-6 col-xl-3">
      <div className="card h-100">
        <div className="card-body">
          <h6 className="text-lg mb-3" style={{ color: '#1B5E20', fontWeight: 'bold' }}>
            Top Languages
          </h6>

          <div className="d-flex flex-column align-items-center" style={{ gap: '12px' }}>
            <div style={{ width: '100%', maxWidth: '220px', height: '220px' }}>
              <ReactApexChart
                options={doughnutOptions}
                series={chartSeries}
                type="donut"
                height={220}
                width="100%"
              />
            </div>
          </div>

          <div style={{ width: '100%' }}>
            {processedData.map((item) => {
              const flagUrl = getFlagUrl(item);
              const hasError = imageErrors[item.LanguageID];

              return (
                <div
                  key={item.LanguageID}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    marginBottom: '8px',
                    fontSize: '11px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: item.color,
                        flexShrink: 0,
                      }}
                    />

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontWeight: '500',
                          fontSize: '15px',
                          color: '#333',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {item.LanguageName}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      width: '30px',
                      height: '20px',
                      flexShrink: 0,
                    }}
                  >
                    {flagUrl && !hasError ? (
                      <img
                        src={flagUrl}
                        alt={item.LanguageName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '2px',
                          border: '1px solid #ddd',
                        }}
                        onError={() => handleImageError(item.LanguageID)}
                      />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          backgroundColor: '#f0f0f0',
                          borderRadius: '2px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '9px',
                          color: '#999',
                          border: '1px solid #ddd',
                        }}
                      >
                        Flag
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

LanguageWiseUser.defaultProps = {
  languageData: [],
};

export default LanguageWiseUser;
